/**
 * The single seam between the app and Supabase's experimental passkey
 * (WebAuthn) API. Every `auth.registerPasskey` / `auth.signInWithPasskey` /
 * `auth.passkey.*` call in the frontend lives here, enforced by
 * `test/guards/passkey-seam.guard.test.ts`, so an SDK shape change is a one-file fix. Consumers
 * import the narrowed domain types below and never an SDK type.
 *
 * The SDK RETURNS errors on the `{ data, error }` channel rather than throwing
 * — including browser ceremony failures, which arrive as `WebAuthnError` with
 * the DOM error `name` and `cause` intact. Both channels are unwrapped here and
 * every failure leaves as a `PasskeyError`.
 *
 * See `docs/plans/2026-07-25-feat-passkey-webauthn-authentication-plan.md`
 * ("Spike findings (U3, 2026-07-26)") for the measured wire shapes this file
 * encodes.
 */

import { supabase } from '@/integrations/supabase/client';
import { isDesktopPlatform } from '@/lib/desktop/bridge';

import type { PasskeyListItem } from '@supabase/supabase-js';

/**
 * Closed taxonomy every passkey failure collapses to. UI copy branches on this
 * — never on an SDK message, which is unstable across auth-server versions and
 * is not translated.
 *
 * Exported as a tuple so consumers can build exhaustive copy maps (and fail
 * their own build when a code is added) rather than re-typing the literals.
 */
export const PASSKEY_ERROR_CODES = [
  'cancelled',
  'credential_not_found',
  'credential_exists',
  'too_many',
  'disabled',
  'aal2_required',
  'email_not_confirmed',
  'user_banned',
  'unknown',
] as const;

export type PasskeyErrorCode = (typeof PASSKEY_ERROR_CODES)[number];

/**
 * Which passkey surface an error came from. Only the management calls (rename,
 * delete) may read `validation_failed` + 404 as a vanished credential - that is
 * the one path the shape was measured on, and applying it everywhere would
 * mistake an ordinary validation failure for a stale list.
 */
export type PasskeyErrorScope = 'ceremony' | 'management';

/**
 * Code-derived, deliberately terse. These are developer-facing strings for logs
 * and stack traces; user-facing copy is translated at the call site. They carry
 * no passkey friendly name — friendly names are PII-adjacent ("Will's iPhone")
 * and must never reach a log line or a Sentry breadcrumb.
 */
const PASSKEY_ERROR_MESSAGES: Readonly<Record<PasskeyErrorCode, string>> = {
  cancelled: 'Passkey ceremony cancelled',
  credential_not_found: 'Passkey not found',
  credential_exists: 'Passkey already registered on this authenticator',
  too_many: 'Passkey limit reached',
  disabled: 'Passkeys are disabled for this project',
  aal2_required: 'Two-factor verification is required to manage passkeys',
  email_not_confirmed: 'Email address is not confirmed',
  user_banned: 'Account is not permitted to sign in',
  unknown: 'Passkey operation failed',
};

export class PasskeyError extends Error {
  readonly code: PasskeyErrorCode;

  /**
   * Copied VERBATIM from the originating `AuthError` when it has one, and
   * `undefined` for browser ceremony errors (which carry no status).
   *
   * `isAuthError` (`supabase-utils.ts`) matches any object with `status: 401`
   * and does NOT traverse `cause`, so without this copy a genuine 401 behind a
   * passkey call would stop tripping the app's auth circuit breaker. Measured
   * both directions: a copied 401 satisfies `isAuthError`, the 403
   * `insufficient_aal` does not.
   */
  readonly status?: number;

  // No `override`: the app's TS lib target (ES2021) predates `Error.cause`,
  // so the base class does not declare it.
  readonly cause?: unknown;

  constructor(code: PasskeyErrorCode, init?: { cause?: unknown; status?: number }) {
    super(PASSKEY_ERROR_MESSAGES[code]);
    this.name = 'PasskeyError';
    this.code = code;
    this.status = init?.status;
    this.cause = init?.cause;
  }
}

/**
 * Server rejections keyed on `AuthApiError.code`.
 *
 * `insufficient_aal` is measured (403, returned pre-ceremony when an MFA user
 * registers or deletes at aal1). The rest are the codes the auth server
 * documents for its passkey surface; anything unlisted falls through to
 * `unknown`, which is the safety net that keeps an unrecognised code from being
 * mistaken for a benign one.
 *
 * A `Map`, not an object literal: the keys are attacker-influenced server
 * strings, and `{}[code]` resolves prototype members — `code: 'constructor'`
 * would hand back `Object` and defeat the `unknown` fall-through entirely.
 * (`Object.hasOwn` is not an option here: the app's TS lib target is ES2021.)
 */
const SERVER_CODE_MAP = new Map<string, PasskeyErrorCode>([
  ['insufficient_aal', 'aal2_required'],
  ['user_banned', 'user_banned'],
  ['passkey_disabled', 'disabled'],
  ['webauthn_credential_not_found', 'credential_not_found'],
  ['email_not_confirmed', 'email_not_confirmed'],
]);

/**
 * Browser ceremony outcomes keyed on `WebAuthnError.code`. `ERROR_PASSTHROUGH_SEE_CAUSE_PROPERTY`
 * is deliberately absent — it says "read the cause", so those land on the name
 * match below. A `Map` for the same prototype-key reason as above.
 */
const CEREMONY_CODE_MAP = new Map<string, PasskeyErrorCode>([
  ['ERROR_CEREMONY_ABORTED', 'cancelled'],
  ['ERROR_AUTHENTICATOR_PREVIOUSLY_REGISTERED', 'credential_exists'],
]);

/**
 * DOM error names, which survive the SDK's `WebAuthnError` wrapper intact.
 * Matched structurally on `name` — never `instanceof DOMException`, which is
 * unreliable across realms and in jsdom (the same reason `isTransientFetchError`
 * matches structurally).
 */
const CEREMONY_NAME_ENTRIES: readonly (readonly [string, PasskeyErrorCode])[] = [
  ['AbortError', 'cancelled'],
  ['NotAllowedError', 'cancelled'],
  ['InvalidStateError', 'credential_exists'],
];

function hasErrorName(error: unknown, name: string): boolean {
  return typeof error === 'object' && error !== null && (error as { name?: unknown }).name === name;
}

function readErrorCode(error: unknown): string | undefined {
  if (typeof error !== 'object' || error === null) {
    return undefined;
  }
  const { code } = error as { code?: unknown };
  return typeof code === 'string' ? code : undefined;
}

function readErrorStatus(error: unknown): number | undefined {
  if (typeof error !== 'object' || error === null) {
    return undefined;
  }
  const { status } = error as { status?: unknown };
  return typeof status === 'number' ? status : undefined;
}

function classifyByCode(
  code: string,
  status: number | undefined,
  scope: PasskeyErrorScope,
): PasskeyErrorCode | undefined {
  const ceremony = CEREMONY_CODE_MAP.get(code);
  if (ceremony !== undefined) {
    return ceremony;
  }
  const server = SERVER_CODE_MAP.get(code);
  if (server !== undefined) {
    return server;
  }
  // `validation_failed` is the auth server's catch-all, so this narrow reading of
  // it is scoped to the path it was MEASURED on: a rename or delete against a
  // missing/already-deleted passkey answers `validation_failed` + 404, there
  // being no dedicated not-found code there. Everywhere else the same shape means
  // an ordinary validation failure (a name over the 120-char cap, say), and
  // reporting that as a vanished credential would send the UI to refetch a list
  // that is perfectly current.
  if (scope === 'management' && code === 'validation_failed' && status === 404) {
    return 'credential_not_found';
  }
  // The passkey limit rejection's exact code is not pinned by the spike (the
  // limit was never reached locally). The auth server names its enrolment caps
  // `too_many_*` (`too_many_enrolled_mfa_factors` is the shipped sibling), so
  // match the family rather than guessing one literal.
  return code.startsWith('too_many') ? 'too_many' : undefined;
}

function classifyByName(error: unknown): PasskeyErrorCode | undefined {
  for (const [name, mapped] of CEREMONY_NAME_ENTRIES) {
    if (hasErrorName(error, name)) {
      return mapped;
    }
  }
  return undefined;
}

/**
 * Classifies anything the passkey surface can fail with into a `PasskeyError`.
 *
 * ORDERING HAZARD — a `catch` block that touches passkey work MUST classify
 * with this function BEFORE consulting `isTransientFetchError` or
 * `isLockContentionError`. Both of those match a bare `name === 'AbortError'`,
 * which is exactly the shape a cancelled or superseded ceremony produces, so
 * either one placed first swallows a user cancel and reports it as a network
 * blip or as auth-lock contention. Measured on the real helpers, both true.
 *
 * TELEMETRY HAZARD — the returned error keeps the original on `cause`, and
 * Sentry's `linkedErrors` integration walks cause chains, so anything the
 * server echoed back travels with it. Consumers MUST log `code` and `status`
 * only, and MUST NOT serialize the error, pass it whole to a logger, or attach
 * it to a breadcrumb. `PasskeyError.message` is derived from the code precisely
 * so the safe field is the obvious one. Unmeasured: whether the rename
 * rejection's body echoes the submitted `friendly_name` (a device nickname —
 * PII-adjacent by design). Treat as if it does.
 */
export function mapPasskeyError(
  error: unknown,
  scope: PasskeyErrorScope = 'ceremony',
): PasskeyError {
  if (error instanceof PasskeyError) {
    return error;
  }
  const status = readErrorStatus(error);
  const code = readErrorCode(error);
  const classified =
    (code === undefined ? undefined : classifyByCode(code, status, scope)) ?? classifyByName(error);
  return new PasskeyError(classified ?? 'unknown', { cause: error, status });
}

/**
 * Synchronous capability check on the Web API alone, matching the SDK's own
 * `browserSupportsWebAuthn` predicate. It answers whether WebAuthn EXISTS in
 * this renderer, not whether the host shell may use it: an iOS WKWebView reports
 * the API and then rejects every ceremony, so screening the host shell is the
 * caller's job. Synchronous by design: an async probe makes the login affordance pop
 * in after paint. Deliberately NOT `isUserVerifyingPlatformAuthenticatorAvailable`
 * — security-key users (common in government offices) must keep the feature.
 */
export function isPasskeySupported(): boolean {
  // The Electron shell passes every structural check below and still cannot run
  // a ceremony. Measured on Electron 43 (`contextIsolation`, `sandbox`, secure
  // localhost origin): `PublicKeyCredential` defined, both credential methods
  // present, `isUserVerifyingPlatformAuthenticatorAvailable()` FALSE, the
  // ceremony pending at 30s with no OS sheet, and the shell's permission handler
  // never invoked at all — WebAuthn's request UI lives in `//chrome`, not the
  // `//content` layer Electron builds on. So the pin goes here, in the capability
  // check, rather than at each affordance. Reopening it needs an Electron-side
  // WebAuthn capability, never a UI change. See
  // `docs/solutions/ui-bugs/desktop-entry-canvas-token-pinning-and-webauthn.md`.
  if (isDesktopPlatform()) {
    return false;
  }
  // `navigator` itself is optional-chained: this runs on the login page's render
  // path, the same path `passkey-hint.ts` is guarded for, and a non-browser
  // renderer (SSR probe, a worker, a jsdom-less test env) has no `navigator` at
  // all — an unguarded destructure there throws instead of reporting false.
  const credentials = globalThis.navigator?.credentials;
  return (
    globalThis.PublicKeyCredential !== undefined &&
    typeof credentials?.create === 'function' &&
    typeof credentials?.get === 'function'
  );
}

/** A passkey as the management UI needs it. Never an SDK type. */
export interface PasskeySummary {
  readonly id: string;
  /** `undefined` when the server holds no name — the column defaults to `''`. */
  readonly friendlyName?: string;
  readonly createdAt: string;
  readonly lastUsedAt?: string;
}

export interface PasskeySignInResult {
  readonly userId: string;
  /** The discoverable flow never collects one, so callers must handle `null`. */
  readonly email: string | null;
  /**
   * `true` when the ceremony was aborted AFTER `navigator.credentials.get()`
   * resolved. The session is REAL — the SDK does not re-check the signal before
   * posting the assertion, and `_verifyPasskeyAuthentication` saves the session
   * and emits `SIGNED_IN`. Callers that abandoned the ceremony MUST compensate
   * with a fire-and-forget `signOut()`; treating this as an ordinary success
   * leaves a signed-in session the user cancelled out of.
   */
  readonly aborted: boolean;
}

export interface PasskeyRegistrationResult {
  readonly id: string;
  /**
   * `false` when the credential was created but the naming call failed OR was
   * skipped because the ceremony had been aborted. The credential is REAL and
   * enrolled — callers surface "created, rename failed" and offer an inline
   * retry. They MUST NOT roll back by deleting it on a rename failure: delete
   * needs aal2, and a silent failure there would strip a working credential.
   */
  readonly renamed: boolean;
  /**
   * `true` when the ceremony was aborted AFTER `navigator.credentials.create()`
   * resolved — measured during the U3 spike: the credential IS enrolled
   * server-side (list count +1, `passkey_created` audit row written) even though
   * the caller asked to stop.
   *
   * This is the compensating-delete contract: a caller that sees `aborted` MUST
   * delete the credential by `id` and refetch the list, because the user
   * believes nothing happened. The rename is deliberately skipped in that case —
   * naming a credential that is about to be deleted only widens the window.
   */
  readonly aborted: boolean;
  /**
   * The classified rename failure, present only when `renamed` is `false` and
   * the rename was actually attempted. Returned rather than swallowed so a 401
   * can still reach the app's auth circuit breaker and `aal2_required` stays
   * distinguishable from a transport blip — the two need different UI.
   */
  readonly renameError?: PasskeyError;
}

function toSummary(item: PasskeyListItem): PasskeySummary {
  // `friendly_name` is NOT NULL DEFAULT '' server-side, so an unnamed passkey
  // arrives as an empty string rather than absent. Both collapse to `undefined`
  // so the UI has exactly one "no name" case to render.
  const friendlyName = item.friendly_name;
  return {
    id: item.id,
    friendlyName: friendlyName === undefined || friendlyName === '' ? undefined : friendlyName,
    createdAt: item.created_at,
    lastUsedAt:
      item.last_used_at === undefined || item.last_used_at === '' ? undefined : item.last_used_at,
  };
}

async function callPasskeyApi<T>(
  run: () => Promise<{ data: T | null; error: unknown }>,
  scope: PasskeyErrorScope = 'ceremony',
): Promise<T | null> {
  let result: { data: T | null; error: unknown };
  try {
    result = await run();
  } catch (error) {
    // The SDK returns rather than throws today. A rejected promise (transport
    // failure, or a future SDK that throws) must still leave as a PasskeyError —
    // every consumer branches on `.code`.
    throw mapPasskeyError(error, scope);
  }
  if (result.error !== null && result.error !== undefined) {
    throw mapPasskeyError(result.error, scope);
  }
  return result.data;
}

/**
 * ONE controller spans BOTH ceremonies. `navigator.credentials.*` promises pend
 * for minutes and resolve on a user gesture, so a second ceremony started while
 * one is open must supersede it rather than race it — and the superseded call
 * must resolve as `cancelled`, not as an error the user sees.
 *
 * The adapter owns this rather than leaning on the SDK's internal supersession,
 * because a caller-supplied signal replaces that mechanism: the SDK relays only
 * the signal it is handed.
 */
let activeCeremony: AbortController | null = null;

interface CeremonyScope {
  readonly signal: AbortSignal;
  release: () => void;
}

function beginCeremony(callerSignal?: AbortSignal): CeremonyScope {
  activeCeremony?.abort();
  const controller = new AbortController();
  activeCeremony = controller;

  const relay = (): void => controller.abort();
  if (callerSignal?.aborted === true) {
    controller.abort();
  } else {
    callerSignal?.addEventListener('abort', relay, { once: true });
  }

  return {
    signal: controller.signal,
    release: () => {
      // Long-lived caller signals (a component's unmount controller) would
      // otherwise accumulate one listener per ceremony.
      callerSignal?.removeEventListener('abort', relay);
      if (activeCeremony === controller) {
        activeCeremony = null;
      }
    },
  };
}

/**
 * Discoverable-credential sign-in. Resolves once the server has verified the
 * assertion and a session exists.
 *
 * The signal covers `navigator.credentials.get()` only, NOT server verification:
 * aborting in the window between the browser resolving and the server replying
 * still produces a real session. That window is reported as `aborted: true` on
 * the result — callers that abandon a ceremony must compensate (fire-and-forget
 * `signOut()`), not assume nothing happened.
 */
export async function signInWithPasskey(signal?: AbortSignal): Promise<PasskeySignInResult> {
  const scope = beginCeremony(signal);
  try {
    const data = await callPasskeyApi(
      async () => await supabase.auth.signInWithPasskey({ options: { signal: scope.signal } }),
    );
    const user = data?.user;
    if (!user) {
      throw new PasskeyError('unknown');
    }
    // Read BEFORE `release()`: release drops the caller-signal relay, so a late
    // abort observed after it would no longer be visible on this signal.
    return { userId: user.id, email: user.email ?? null, aborted: scope.signal.aborted };
  } finally {
    scope.release();
  }
}

interface RegistrationCeremonyOutcome {
  readonly credentialId: string;
  readonly aborted: boolean;
}

async function runRegistrationCeremony(signal?: AbortSignal): Promise<RegistrationCeremonyOutcome> {
  const scope = beginCeremony(signal);
  try {
    const data = await callPasskeyApi(
      async () => await supabase.auth.registerPasskey({ options: { signal: scope.signal } }),
    );
    if (!data) {
      throw new PasskeyError('unknown');
    }
    // Same ordering as sign-in: captured before `release()` drops the relay.
    return { credentialId: data.id, aborted: scope.signal.aborted };
  } finally {
    scope.release();
  }
}

/**
 * Enrolment is TWO calls: `registerPasskey()` accepts no friendly name (the
 * server names every credential `"Passkey"`), so the name is applied with a
 * follow-up `passkey.update()`.
 *
 * Three distinct outcomes, and callers must handle all three:
 *
 * 1. `{ renamed: true, aborted: false }` — enrolled and named.
 * 2. `{ renamed: false, aborted: false, renameError }` — enrolled, naming
 *    failed. NOT a failed enrolment: the credential exists and works. Surface
 *    "created, rename failed" with an inline retry and branch UI on
 *    `renameError.code`. MUST NOT roll back by deleting.
 * 3. `{ renamed: false, aborted: true }` — enrolled, but the caller aborted
 *    after the browser had already produced the credential. The user believes
 *    nothing happened, so the caller MUST delete the credential by `id` and
 *    refetch the list (the compensating delete). The rename is skipped.
 */
export async function registerPasskeyWithName(
  friendlyName: string,
  signal?: AbortSignal,
): Promise<PasskeyRegistrationResult> {
  const { credentialId, aborted } = await runRegistrationCeremony(signal);

  if (aborted) {
    return { id: credentialId, renamed: false, aborted: true };
  }

  try {
    await renamePasskey(credentialId, friendlyName);
    return { id: credentialId, renamed: true, aborted: false };
  } catch (error) {
    return {
      id: credentialId,
      renamed: false,
      aborted: false,
      renameError: mapPasskeyError(error),
    };
  }
}

export async function listPasskeys(): Promise<PasskeySummary[]> {
  const data = await callPasskeyApi(async () => await supabase.auth.passkey.list(), 'management');
  return (data ?? []).map((item) => toSummary(item));
}

export async function renamePasskey(
  passkeyId: string,
  friendlyName: string,
): Promise<PasskeySummary> {
  const data = await callPasskeyApi(
    async () => await supabase.auth.passkey.update({ passkeyId, friendlyName }),
    'management',
  );
  if (!data) {
    throw new PasskeyError('unknown');
  }
  return toSummary(data);
}

export async function deletePasskey(passkeyId: string): Promise<void> {
  // Resolves `{ data: null, error: null }` on success, so the null return is
  // the happy path here rather than a contract violation.
  await callPasskeyApi(async () => await supabase.auth.passkey.delete({ passkeyId }), 'management');
}
